// Warp transition overlay: the fullscreen tunnel flight between the arena and
// wormhole subspace — radial light streaks that burst outward (warp-in) or
// rush back inward (warp-out), crossfading from the arena's ice blue to the
// subspace violet/red as they peak. A single camera-anchored shader quad in
// the style of render/wormhole.ts; all animation derives from the sim tick
// (uTime) and the bonus-phase progress (uWarp), so identical runs render
// identical transitions. Sim owns the timing — the renderer only maps
// bonus.phase + phase_start_tick onto these uniforms (see renderer.ts).

import * as THREE from 'three';

// Streak lane count around the tunnel; hashed per lane so the burst is
// uneven (a uniform pinwheel reads as a loading spinner, not a warp).
const LANES = 28.0;

export class WarpTransition {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor() {
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        // Sim seconds (world.tick / 60); keeps the streaks deterministic.
        uTime: { value: 0 },
        // 0..1 tunnel intensity envelope (sin of phase progress), sim-driven.
        uWarp: { value: 0 },
        // +1 flying into subspace (streaks burst outward), -1 flying home.
        uDir: { value: 1 },
        // 0..1 palette crossfade: arena ice blue → subspace violet/red.
        uMix: { value: 0 },
        // View aspect (w/h) so the tunnel stays circular on any screen.
        uAspect: { value: 1 },
      },
      vertexShader: WARP_VERT,
      fragmentShader: WARP_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendEquation: THREE.AddEquation
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    // Above the world and the portals, below the DOM HUD.
    this.mesh.renderOrder = 8;
    this.mesh.visible = false;
  }

  /**
   * Anchor the quad to the camera and push the frame's uniforms. `warp` is
   * the 0..1 intensity envelope; hidden when it bottoms out.
   */
  update(
    center: { x: number; y: number },
    viewW: number,
    viewH: number,
    time: number,
    warp: number,
    dir: number,
    mix: number
  ): void {
    if (warp <= 0.004) {
      this.mesh.visible = false;
      this.material.uniforms.uWarp.value = 0;
      return;
    }
    this.mesh.visible = true;
    this.mesh.position.set(center.x, center.y, 0.09);
    this.mesh.scale.set(viewW + 8, viewH + 8, 1);
    const u = this.material.uniforms;
    u.uTime.value = time;
    u.uWarp.value = warp;
    u.uDir.value = dir;
    u.uMix.value = mix;
    u.uAspect.value = viewW / Math.max(viewH, 1e-6);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

const WARP_VERT = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const WARP_FRAG = /* glsl */ `
precision highp float;

uniform float uTime;
uniform float uWarp;
uniform float uDir;
uniform float uMix;
uniform float uAspect;
varying vec2 vUv;

float hash1(float n) {
  return fract(sin(n * 127.1) * 43758.5453);
}

void main() {
  if (uWarp <= 0.004) discard;

  // Screen-center radial frame, aspect-corrected so lanes stay round.
  vec2 p = vUv * 2.0 - 1.0;
  p.x *= uAspect;
  float rad = length(p);
  float th = atan(p.y, p.x);

  // Streak lanes: each lane gets a hashed on/off, brightness, and radial
  // speed; only the lane's thin angular core is lit, so the tunnel reads as
  // discrete starlight streaks with dark gaps — not a uniform pinwheel.
  float lane = floor((th / 6.28318 + 0.5) * ${LANES.toFixed(1)});
  float laneU = fract((th / 6.28318 + 0.5) * ${LANES.toFixed(1)});
  float laneJitter = hash1(lane);
  float laneOn = step(hash1(lane + 7.31), 0.5);
  float core = smoothstep(0.28, 0.08, abs(laneU - 0.5));
  float laneGain = (0.45 + 0.55 * hash1(lane + 13.7)) * laneOn * core;
  float speed = (1.6 + 1.3 * laneJitter) * uDir;
  float saw = fract(rad * (2.2 + 1.6 * laneJitter) - uTime * speed);
  // Comet profile: bright head, fading tail; longer as the warp peaks.
  float streak = pow(1.0 - saw, 2.4);
  // The tunnel opens from the center: streaks live in a mid-radius band that
  // flares outward with the envelope, and the ship at the center stays clear.
  float band = smoothstep(0.10, 0.45 + 0.20 * uWarp, rad) * (1.0 - smoothstep(1.15, 1.6, rad));
  float glow = streak * laneGain * band;

  // Palette crossfade: ice blue -> violet with a red edge.
  vec3 cold = vec3(0.30, 0.62, 1.00);
  vec3 hot = vec3(0.72, 0.16, 0.95) + vec3(0.18, 0.02, -0.10) * laneJitter;
  vec3 col = mix(cold, hot, uMix) * glow;

  // White-hot throat flash at the envelope peak (the moment the planes swap).
  float flash = exp(-rad * rad * 5.0) * pow(uWarp, 3.0);
  col += mix(vec3(0.7, 0.85, 1.0), vec3(1.0, 0.55, 0.85), uMix) * flash * 0.45;

  col *= uWarp;
  if (max(col.r, max(col.g, col.b)) <= 0.004) discard;
  gl_FragColor = vec4(col, 1.0);
}
`;
